import React from 'react';
import classes from './DealerStockFilterPanel.module.scss';
import { groupMakesByCountry } from '../../helper/makeCountries';
import {
    MILEAGE_STEPS,
    getConsolidatedColors,
    getBodyTypeOptions,
} from '../../helper/dealerStockFilters';

/**
 * The dealer-stock filter sidebar.
 *
 * Deliberately mirrors the marketing site's DealerStockFilter component —
 * same nine controls, same order, same labels and option wording — because
 * this portal shows the same inventory and a buyer moving between the two
 * should not have to relearn the filter. The styling is the portal's own
 * (SCSS module + the shared token palette), so it reads as native here rather
 * than as transplanted Tailwind.
 *
 * The panel is CONTROLLED but not live: edits accumulate in the parent's draft
 * state and only reach the API on "Apply Filters". Every control firing a
 * request on change made the grid thrash while a user set up a multi-filter
 * search, which is why the marketing site has the same Apply/Reset pair.
 */
const DealerStockFilterPanel = ({
    draft,
    filterOptions,
    optionsLoading,
    onDraftChange,
    onApply,
    onReset,
    open,
    onToggle,
    savedSearches,
}) => {
    const handle = (event) => onDraftChange(event.target.name, event.target.value);

    // Models come back as [{ make, model }]. Narrow to the selected make, then
    // de-duplicate: the same model name appears once per make in the feed.
    const models = React.useMemo(() => {
        const all = filterOptions.models || [];
        const forMake = draft.make ? all.filter((m) => m.make === draft.make) : all;
        return [...new Set(forMake.map((m) => m.model).filter(Boolean))].sort((a, b) =>
            a.localeCompare(b)
        );
    }, [filterOptions.models, draft.make]);

    const colorOptions = React.useMemo(
        () => getConsolidatedColors(filterOptions.colors || []),
        [filterOptions.colors]
    );

    const bodyTypeOptions = React.useMemo(
        () => getBodyTypeOptions(filterOptions.bodyTypes || []),
        [filterOptions.bodyTypes]
    );

    const makeGroups = React.useMemo(
        () => groupMakesByCountry(filterOptions.makes || []),
        [filterOptions.makes]
    );

    // Ascending for the Min box, descending for the Max box: whichever bound a
    // user is setting, the years they are most likely to want are at the top of
    // the list rather than 30 rows down.
    const yearsAsc = React.useMemo(
        () => [...(filterOptions.years || [])].sort((a, b) => a - b),
        [filterOptions.years]
    );
    const yearsDesc = React.useMemo(() => [...yearsAsc].reverse(), [yearsAsc]);

    return (
        <>
            {/* Mobile opener — the panel itself is hidden below the medium breakpoint */}
            <button
                type="button"
                className={classes.mobileToggle}
                onClick={onToggle}
                aria-expanded={open}
                aria-controls="dealer-stock-filters"
            >
                <i className="fas fa-filter" aria-hidden="true"></i>
                Filter Vehicles
            </button>

            <aside
                id="dealer-stock-filters"
                className={`${classes.panel} ${open ? classes.panelOpen : ''}`}
            >
                {/* Saving a search and its alerts live in the same card as the
                    filters they save, not in a second card above it. Outside the
                    options spinner: the list does not depend on the options. */}
                {savedSearches && <div className={classes.savedSearches}>{savedSearches}</div>}

                <h2 className={classes.panelTitle}>Filter Vehicles</h2>

                {optionsLoading ? (
                    <div className={classes.optionsLoading}>
                        <span className={classes.spinner} aria-hidden="true"></span>
                    </div>
                ) : (
                    <div className={classes.controls}>
                        <div className={classes.field}>
                            <label htmlFor="ds-make">Make</label>
                            <select id="ds-make" name="make" value={draft.make} onChange={handle}>
                                <option value="">All Makes</option>
                                {makeGroups.map((group) => (
                                    <optgroup key={group.country} label={group.country}>
                                        {group.makes.map((make) => (
                                            <option key={make} value={make}>{make}</option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>
                        </div>

                        <div className={classes.field}>
                            <label htmlFor="ds-model">Model</label>
                            <select id="ds-model" name="model" value={draft.model} onChange={handle}>
                                <option value="">All Models</option>
                                {models.map((model) => (
                                    <option key={model} value={model}>{model}</option>
                                ))}
                            </select>
                        </div>

                        <div className={classes.field}>
                            <span className={classes.fieldLabel}>Year Range</span>
                            <div className={classes.fieldPair}>
                                <select name="minYear" value={draft.minYear} onChange={handle} aria-label="Minimum year">
                                    <option value="">Min Year</option>
                                    {yearsAsc.map((year) => (
                                        <option key={`min-${year}`} value={year}>{year}</option>
                                    ))}
                                </select>
                                <select name="maxYear" value={draft.maxYear} onChange={handle} aria-label="Maximum year">
                                    <option value="">Max Year</option>
                                    {yearsDesc.map((year) => (
                                        <option key={`max-${year}`} value={year}>{year}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className={classes.field}>
                            <label htmlFor="ds-color">Color</label>
                            <select id="ds-color" name="color" value={draft.color} onChange={handle}>
                                <option value="">All Colors</option>
                                {colorOptions.map((color) => (
                                    <option key={color.value} value={color.value}>{color.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className={classes.field}>
                            <label htmlFor="ds-transmission">Transmission</label>
                            <select id="ds-transmission" name="transmission" value={draft.transmission} onChange={handle}>
                                <option value="">All Transmissions</option>
                                <option value="Manual">Manual</option>
                                <option value="Auto">Automatic</option>
                            </select>
                        </div>

                        <div className={classes.field}>
                            <label htmlFor="ds-bodyType">Body Type</label>
                            {/* VALUE is the raw Japanese feed string (what /api/cars
                                matches on); LABEL is the English translation. */}
                            <select id="ds-bodyType" name="bodyType" value={draft.bodyType} onChange={handle}>
                                <option value="">All Body Types</option>
                                {bodyTypeOptions.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className={classes.field}>
                            <label htmlFor="ds-chassis">Chassis Generation</label>
                            <select
                                id="ds-chassis"
                                name="chassisGeneration"
                                value={draft.chassisGeneration}
                                onChange={handle}
                            >
                                <option value="">All Generations</option>
                                {[...(filterOptions.chassisGenerations || [])]
                                    .sort((a, b) => a.localeCompare(b))
                                    .map((generation) => (
                                        <option key={generation} value={generation}>{generation}</option>
                                    ))}
                            </select>
                        </div>

                        <div className={classes.field}>
                            <span className={classes.fieldLabel}>Price Range ($)</span>
                            {/* Dollars, not yen. /api/cars converts these to JPY
                                server-side for carlistings and applies them as-is to
                                current inventory — see buildDealerPriceClause. */}
                            <div className={classes.fieldPair}>
                                <input
                                    type="number"
                                    name="minPrice"
                                    min="0"
                                    placeholder="Min"
                                    value={draft.minPrice}
                                    onChange={handle}
                                    aria-label="Minimum price in US dollars"
                                />
                                <input
                                    type="number"
                                    name="maxPrice"
                                    min="0"
                                    placeholder="Max"
                                    value={draft.maxPrice}
                                    onChange={handle}
                                    aria-label="Maximum price in US dollars"
                                />
                            </div>
                        </div>

                        <div className={classes.field}>
                            <span className={classes.fieldLabel}>Mileage (km)</span>
                            {/* Fixed steps, not free text: the feed stores dealer-stock
                                mileage in thousands, so a box labelled "km" that
                                filtered at 1000x was actively misleading. */}
                            <div className={classes.fieldPair}>
                                <select name="minMileage" value={draft.minMileage} onChange={handle} aria-label="Minimum mileage">
                                    <option value="">No Min</option>
                                    {MILEAGE_STEPS.map((km) => (
                                        <option key={`min-${km}`} value={km}>{km.toLocaleString()} km</option>
                                    ))}
                                </select>
                                <select name="maxMileage" value={draft.maxMileage} onChange={handle} aria-label="Maximum mileage">
                                    <option value="">No Max</option>
                                    {MILEAGE_STEPS.map((km) => (
                                        <option key={`max-${km}`} value={km}>{km.toLocaleString()} km</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className={classes.actions}>
                            <button type="button" className={classes.applyButton} onClick={onApply}>
                                Apply Filters
                            </button>
                            <button type="button" className={classes.resetButton} onClick={onReset}>
                                Reset
                            </button>
                        </div>
                    </div>
                )}
            </aside>
        </>
    );
};

export default DealerStockFilterPanel;
